import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Users, Plus, Search, MapPin, Briefcase, Lock, Globe, MessageCircle,
  ChevronRight, ChevronLeft, Star, X, Check, Settings, UserPlus, Sparkles, BookOpen,
  Upload, UserMinus, CheckCircle, XCircle, Image, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { ActivateModal } from '../components/PremiumGate';

// ==================== SCROLLABLE ROW WITH ARROWS ====================
const ScrollableRow = ({ children }) => {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const checkScroll = () => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 0);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    checkScroll();
    const el = ref.current;
    if (el) el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el?.removeEventListener('scroll', checkScroll);
  }, [children]);

  const scroll = (dir) => {
    ref.current?.scrollBy({ left: dir * 350, behavior: 'smooth' });
  };

  return (
    <div className="relative group">
      {canLeft && (
        <button
          onClick={() => scroll(-1)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-background/90 border border-border shadow-lg flex items-center justify-center hover:bg-muted transition-all opacity-0 group-hover:opacity-100"
          data-testid="scroll-left-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      <div ref={ref} className="flex gap-5 overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-border scroll-smooth">
        {children}
      </div>
      {canRight && (
        <button
          onClick={() => scroll(1)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-background/90 border border-border shadow-lg flex items-center justify-center hover:bg-muted transition-all opacity-0 group-hover:opacity-100"
          data-testid="scroll-right-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

// ==================== GROUP HERO CARD (horizontal scroll style) ====================
const GroupHeroCard = ({ group, currentUserId, onJoin, onManage, onViewDetail }) => {
  const isMember = group.is_member;
  const memberCount = group.member_count || 0;
  const isAdmin = group.creator_id === currentUserId;

  const TypeIcon = group.group_type === 'location' ? MapPin
    : group.group_type === 'industry' ? Briefcase
    : Users;

  return (
    <Card className="flex-shrink-0 w-[340px] overflow-hidden hover:shadow-xl transition-all cursor-pointer group/card border-border/60" data-testid={`group-${group.id}`} onClick={() => onViewDetail(group)}>
      {/* Cover image - full width, prominent */}
      <div className="h-48 relative overflow-hidden">
        {(group.cover_image || group.image_url) ? (
          <img src={resolveImageUrl(group.cover_image || group.image_url)} alt="" className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 flex items-center justify-center">
            <TypeIcon className="w-12 h-12 text-white/15" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        {/* Privacy badge top-right */}
        <div className="absolute top-3 right-3">
          {group.is_private ? (
            <Badge variant="secondary" className="bg-black/40 text-white text-[10px] px-2 py-0.5 backdrop-blur-md border-white/10"><Lock className="w-2.5 h-2.5 mr-1" />Private</Badge>
          ) : (
            <Badge variant="secondary" className="bg-black/40 text-white text-[10px] px-2 py-0.5 backdrop-blur-md border-white/10"><Globe className="w-2.5 h-2.5 mr-1" />Public</Badge>
          )}
        </div>
        {/* Title overlay at bottom */}
        <div className="absolute bottom-3 left-4 right-4">
          <h3 className="font-bold text-base text-white line-clamp-1 drop-shadow-lg">{group.name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[11px] text-white/80 flex items-center gap-1"><Users className="w-3 h-3" />{memberCount} members</span>
            {group.location && <span className="text-[11px] text-white/60 flex items-center gap-1"><MapPin className="w-3 h-3" />{group.location}</span>}
          </div>
        </div>
      </div>

      <CardContent className="p-4">
        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3 min-h-[2.5rem] leading-relaxed">
          {group.description || 'A community for professionals to connect and share insights.'}
        </p>

        {/* Type tag */}
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="outline" className="text-[11px] px-2 py-0.5 capitalize font-normal">
            <TypeIcon className="w-3 h-3 mr-1" />{group.group_type}
          </Badge>
        </div>

        {/* Action */}
        <div className="pt-3 border-t border-border/50" onClick={e => e.stopPropagation()}>
          {isAdmin ? (
            <Button size="sm" variant="ghost" className="w-full text-xs h-9" onClick={() => onManage(group)} data-testid={`manage-${group.id}`}><Settings className="w-3.5 h-3.5 mr-1.5" />Manage</Button>
          ) : (
            <Button
              size="sm"
              variant={isMember ? 'outline' : 'default'}
              onClick={() => onJoin(group.id)}
              className={`w-full text-xs h-9 ${isMember ? 'text-green-600 border-green-600' : ''}`}
              data-testid={`join-${group.id}`}
            >
              {isMember ? (<><Check className="w-3.5 h-3.5 mr-1" />Member</>) : (<><UserPlus className="w-3.5 h-3.5 mr-1" />Join Group</>)}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== SECTION ROW (horizontal scroll) ====================
const GroupSectionRow = ({ title, subtitle, icon: Icon, iconColor, groups, currentUserId, onJoin, onManage, onViewDetail, emptyMsg }) => (
  <section className="space-y-4" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-lg font-bold font-['Outfit']">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
    </div>

    {groups.length > 0 ? (
      <ScrollableRow>
        {groups.map(group => (
          <GroupHeroCard key={group.id} group={group} currentUserId={currentUserId} onJoin={onJoin} onManage={onManage} onViewDetail={onViewDetail} />
        ))}
      </ScrollableRow>
    ) : (
      <Card>
        <CardContent className="py-10 text-center">
          <Icon className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">{emptyMsg}</p>
        </CardContent>
      </Card>
    )}
  </section>
);

// ==================== CREATE GROUP MODAL ====================
const CreateGroupModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState({ name: '', description: '', group_type: 'industry', location: '', target_gender: 'all', is_private: false, cover_image: '' });
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('connectmail_token')}` },
        body: formData
      });
      const data = await resp.json();
      if (data.url) {
        setForm(f => ({ ...f, cover_image: data.url }));
        toast.success('Image uploaded!');
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Please enter a group name'); return; }
    setCreating(true);
    try { await onCreate(form); toast.success('Group created successfully!'); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create group'); }
    finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-group-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Create a Group</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Cover Image Upload */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Cover Image</label>
            {form.cover_image ? (
              <div className="relative rounded-lg overflow-hidden bg-muted/30 border border-border">
                <img src={resolveImageUrl(form.cover_image)} alt="Cover" className="w-full block" style={{ maxHeight: '280px', objectFit: 'contain', margin: '0 auto' }} />
                <button type="button" onClick={() => setForm({...form, cover_image: ''})} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80 z-10">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors" data-testid="group-image-upload">
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                {uploading ? (
                  <span className="text-xs text-muted-foreground">Uploading...</span>
                ) : (
                  <>
                    <Image className="w-6 h-6 text-muted-foreground mb-1" />
                    <span className="text-xs text-muted-foreground">Click to upload a cover image</span>
                  </>
                )}
              </label>
            )}
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Group Name *</label>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Troy Real Estate Professionals" data-testid="group-name-input" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What is this group about?" className="w-full h-20 px-3 py-2 text-sm border border-border rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="group-description-input" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Group Type</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'industry', label: 'By Profession', icon: Briefcase },
                { id: 'location', label: 'By Location', icon: MapPin },
                { id: 'gender', label: 'By Gender', icon: Users },
                { id: 'interest', label: 'By Interest', icon: Star },
              ].map(type => {
                const TypeIcon = type.icon;
                return (
                  <button key={type.id} type="button" onClick={() => setForm({...form, group_type: type.id})}
                    className={`py-2.5 px-3 rounded-lg border text-xs font-medium transition-all flex items-center gap-1.5 ${form.group_type === type.id ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'}`}
                    data-testid={`group-type-${type.id}`}>
                    <TypeIcon className="w-3.5 h-3.5" />{type.label}
                  </button>
                );
              })}
            </div>
          </div>
          {form.group_type === 'location' && (
            <div>
              <label className="text-sm font-medium mb-1.5 block">Location</label>
              <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="e.g., Troy, MI" data-testid="group-location-input" />
            </div>
          )}
          {form.group_type === 'gender' && (
            <div>
              <label className="text-sm font-medium mb-1.5 block">Target Audience</label>
              <div className="grid grid-cols-3 gap-2">
                {['men', 'women', 'all'].map(g => (
                  <button key={g} type="button" onClick={() => setForm({...form, target_gender: g})}
                    className={`py-2 px-3 rounded-lg border text-xs font-medium capitalize transition-all ${form.target_gender === g ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'}`}
                    data-testid={`gender-${g}`}>{g === 'all' ? 'Everyone' : g}</button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 py-2">
            <button type="button" onClick={() => setForm({...form, is_private: !form.is_private})} className={`w-10 h-5 rounded-full transition-colors ${form.is_private ? 'bg-primary' : 'bg-muted'}`} data-testid="group-private-toggle">
              <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_private ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
            <div><p className="text-sm font-medium">Private Group</p><p className="text-xs text-muted-foreground">Members must be approved to join</p></div>
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={creating} className="flex-1" data-testid="create-group-submit">{creating ? 'Creating...' : 'Create Group'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== MANAGE GROUP MODAL ====================
const ManageGroupModal = ({ group, onClose, onUpdate }) => {
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [isCreator, setIsCreator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [tab, setTab] = useState('members');
  const [editForm, setEditForm] = useState({
    name: group.name || '',
    description: group.description || '',
    location: group.location || '',
    cover_image: group.cover_image || group.image_url || '',
    is_private: group.is_private || false,
    group_type: group.group_type || 'industry',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getGroupMembers(group.id);
        setMembers(data.members || []);
        setPending(data.pending_members || []);
        setIsCreator(data.is_creator);
      } catch (err) { toast.error('Failed to load members'); }
      finally { setLoading(false); }
    })();
  }, [group.id]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('connectmail_token')}` },
        body: formData
      });
      const data = await resp.json();
      if (data.url) { setEditForm(f => ({ ...f, cover_image: data.url })); toast.success('Image uploaded!'); }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!editForm.name.trim()) { toast.error('Group name is required'); return; }
    setSaving(true);
    try {
      await api.updateGroup(group.id, editForm);
      onUpdate();
      toast.success('Group updated!');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleApprove = async (userId) => {
    setActionLoading(userId);
    try {
      await api.approveGroupMember(group.id, userId);
      setPending(p => p.filter(m => m.user_id !== userId));
      const data = await api.getGroupMembers(group.id);
      setMembers(data.members || []);
      onUpdate();
      toast.success('Member approved!');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (userId) => {
    setActionLoading(userId);
    try {
      await api.rejectGroupMember(group.id, userId);
      setPending(p => p.filter(m => m.user_id !== userId));
      toast.success('Request rejected');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleKick = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from this group?`)) return;
    setActionLoading(userId);
    try {
      await api.kickGroupMember(group.id, userId);
      setMembers(m => m.filter(mem => mem.user_id !== userId));
      onUpdate();
      toast.success('Member removed');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="manage-group-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-semibold">Manage: {group.name}</h2>
            <p className="text-xs text-muted-foreground">{members.length} members</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        {isCreator && (
          <div className="flex border-b px-5">
            <button onClick={() => setTab('members')} className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${tab === 'members' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="manage-tab-members">
              <Users className="w-3.5 h-3.5 inline mr-1" />Members
            </button>
            <button onClick={() => setTab('edit')} className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${tab === 'edit' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="manage-tab-edit">
              <Pencil className="w-3.5 h-3.5 inline mr-1" />Edit Group
            </button>
          </div>
        )}

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-8">Loading...</p>
          ) : tab === 'edit' && isCreator ? (
            /* ========== EDIT GROUP TAB ========== */
            <div className="space-y-4" data-testid="edit-group-section">
              {/* Cover Image */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Cover Image</label>
                {editForm.cover_image ? (
                  <div className="relative rounded-lg overflow-hidden bg-muted/30 border border-border">
                    <img src={resolveImageUrl(editForm.cover_image)} alt="Cover" className="w-full block" style={{ maxHeight: '280px', objectFit: 'contain', margin: '0 auto' }} />
                    <button type="button" onClick={() => setEditForm({...editForm, cover_image: ''})} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80 z-10">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors" data-testid="edit-group-image-upload">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    {uploading ? <span className="text-xs text-muted-foreground">Uploading...</span> : (
                      <><Image className="w-6 h-6 text-muted-foreground mb-1" /><span className="text-xs text-muted-foreground">Upload cover image</span></>
                    )}
                  </label>
                )}
              </div>

              {/* Group Name */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Group Name *</label>
                <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} placeholder="Group name" data-testid="edit-group-name" />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
                <textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} placeholder="What is this group about?" className="w-full px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none" rows={3} data-testid="edit-group-description" />
              </div>

              {/* Location */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Location</label>
                <Input value={editForm.location} onChange={e => setEditForm({...editForm, location: e.target.value})} placeholder="e.g. Troy, MI" data-testid="edit-group-location" />
              </div>

              {/* Group Type */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Group Type</label>
                <select value={editForm.group_type} onChange={e => setEditForm({...editForm, group_type: e.target.value})} className="w-full px-3 py-2 text-sm rounded-lg border border-input bg-background" data-testid="edit-group-type">
                  <option value="industry">Professional / Industry</option>
                  <option value="location">Location Based</option>
                  <option value="gender">Gender Specific</option>
                  <option value="interest">Interest Based</option>
                </select>
              </div>

              {/* Private toggle */}
              <div className="flex items-center gap-3 py-1">
                <button type="button" onClick={() => setEditForm({...editForm, is_private: !editForm.is_private})} className={`w-10 h-5 rounded-full transition-colors ${editForm.is_private ? 'bg-primary' : 'bg-muted'}`} data-testid="edit-group-private-toggle">
                  <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${editForm.is_private ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
                <div><p className="text-sm font-medium">Private Group</p><p className="text-xs text-muted-foreground">Members must be approved</p></div>
              </div>

              {/* Save Button */}
              <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-group-changes">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          ) : (
            /* ========== MEMBERS TAB ========== */
            <>
              {/* Pending Requests */}
              {isCreator && pending.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <UserPlus className="w-4 h-4 text-amber-500" />
                    Pending Requests ({pending.length})
                  </h3>
                  <div className="space-y-2">
                    {pending.map(m => (
                      <div key={m.user_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-amber-50 border border-amber-100" data-testid={`pending-${m.user_id}`}>
                        <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-xs font-bold text-amber-700">
                          {m.photo ? <img src={resolveImageUrl(m.photo)} alt="" className="w-full h-full rounded-full object-cover" /> : (m.name || '?')[0]}
                        </div>
                        <span className="flex-1 text-sm font-medium">{m.name}</span>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-green-600" disabled={actionLoading === m.user_id} onClick={() => handleApprove(m.user_id)} data-testid={`approve-${m.user_id}`}>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />Approve
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" disabled={actionLoading === m.user_id} onClick={() => handleReject(m.user_id)} data-testid={`reject-${m.user_id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Members */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-primary" />
                  Members ({members.length})
                </h3>
                <div className="space-y-1.5">
                  {members.map(m => (
                    <div key={m.user_id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50" data-testid={`member-${m.user_id}`}>
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        {m.photo ? <img src={resolveImageUrl(m.photo)} alt="" className="w-full h-full rounded-full object-cover" /> : (m.name || '?')[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{m.name}</p>
                        <p className="text-[10px] text-muted-foreground">{m.role === 'admin' ? 'Creator' : 'Member'}</p>
                      </div>
                      {isCreator && m.role !== 'admin' && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" disabled={actionLoading === m.user_id} onClick={() => handleKick(m.user_id, m.name)} data-testid={`kick-${m.user_id}`}>
                          <UserMinus className="w-3.5 h-3.5 mr-1" />Remove
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== GROUP DETAIL MODAL ====================
const GroupDetailModal = ({ group, currentUserId, onClose, onJoin, onManage, onNavigate }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = group.creator_id === currentUserId;

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getGroup(group.id);
        setDetail(data);
      } catch {
        setDetail(group);
      } finally { setLoading(false); }
    })();
  }, [group.id]);

  const g = detail || group;
  const memberCount = g.member_count || g.members?.length || 0;
  const TypeIcon = g.group_type === 'location' ? MapPin
    : g.group_type === 'industry' ? Briefcase
    : Users;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="group-detail-modal">
        {/* Cover Image */}
        <div className="h-56 relative overflow-hidden flex-shrink-0">
          {(g.cover_image || g.image_url) ? (
            <img src={resolveImageUrl(g.cover_image || g.image_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 flex items-center justify-center">
              <TypeIcon className="w-16 h-16 text-white/10" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <button onClick={onClose} className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-black/70 transition-colors" data-testid="close-group-detail">
            <X className="w-5 h-5" />
          </button>
          <div className="absolute bottom-4 left-5 right-5">
            <div className="flex items-center gap-2 mb-1.5">
              {g.is_private ? (
                <Badge className="bg-amber-500/80 text-white text-[10px] border-0"><Lock className="w-2.5 h-2.5 mr-1" />Private</Badge>
              ) : (
                <Badge className="bg-emerald-500/80 text-white text-[10px] border-0"><Globe className="w-2.5 h-2.5 mr-1" />Public</Badge>
              )}
              <Badge variant="outline" className="bg-white/15 text-white text-[10px] border-white/20 backdrop-blur-sm capitalize">
                <TypeIcon className="w-2.5 h-2.5 mr-1" />{g.group_type}
              </Badge>
            </div>
            <h2 className="text-2xl font-bold text-white drop-shadow-lg">{g.name}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <>
              {/* Stats row */}
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="w-4 h-4 text-primary" />
                  <span><strong className="text-foreground">{memberCount}</strong> members</span>
                </div>
                {g.location && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4 text-green-500" />
                    <span>{g.location}</span>
                  </div>
                )}
                {g.post_count > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MessageCircle className="w-4 h-4 text-blue-500" />
                    <span><strong className="text-foreground">{g.post_count}</strong> posts</span>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-semibold mb-2">About this group</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {g.description || 'A community for professionals to connect, share insights, and grow together.'}
                </p>
              </div>

              {/* Creator info */}
              {g.creator_name && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                    {g.creator_name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-medium">Created by {g.creator_name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {g.created_at ? new Date(g.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : ''}
                    </p>
                  </div>
                </div>
              )}

              {/* Recent members preview */}
              {g.members && g.members.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Members</h3>
                  <div className="flex items-center gap-1">
                    {g.members.slice(0, 8).map((m, i) => (
                      <div key={m.user_id || i} className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary border-2 border-background -ml-1 first:ml-0" title={m.name}>
                        {m.photo ? <img src={resolveImageUrl(m.photo)} alt="" className="w-full h-full rounded-full object-cover" /> : (m.name || '?')[0]}
                      </div>
                    ))}
                    {memberCount > 8 && (
                      <span className="text-xs text-muted-foreground ml-2">+{memberCount - 8} more</span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Action */}
        <div className="border-t p-5 flex-shrink-0 flex gap-3">
          {isAdmin ? (
            <>
              <Button variant="outline" className="flex-1" onClick={onClose}>Close</Button>
              <Button variant="outline" className="flex-1" onClick={() => { onClose(); onNavigate(group.id); }} data-testid="detail-view-btn">
                View Group
              </Button>
              <Button className="flex-1" onClick={() => { onClose(); onManage(group); }} data-testid="detail-manage-btn">
                <Settings className="w-4 h-4 mr-2" />Manage
              </Button>
            </>
          ) : g.is_member ? (
            <>
              <Button variant="outline" className="flex-1" onClick={onClose}>Close</Button>
              <Button className="flex-1" onClick={() => { onClose(); onNavigate(group.id); }} data-testid="detail-view-btn">
                View Group
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="flex-1" onClick={onClose}>Close</Button>
              <Button
                className="flex-1"
                onClick={() => { onJoin(group.id); onClose(); }}
                data-testid="detail-join-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />Join Group
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN GROUPS PAGE ====================
export const Groups = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get('filter') || 'all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const isPremium = user?.is_premium === true;
  const [manageGroup, setManageGroup] = useState(null);
  const [detailGroup, setDetailGroup] = useState(null);

  const fetchGroups = useCallback(async (activeFilter) => {
    try {
      const data = await api.getGroupsFiltered(activeFilter);
      setGroups(data.groups || []);
    } catch (error) { console.error('Failed to fetch groups:', error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGroups(filter); }, [fetchGroups, filter]);
  useEffect(() => {
    const urlFilter = searchParams.get('filter');
    if (urlFilter && urlFilter !== filter) setFilter(urlFilter);
  }, [searchParams]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    newFilter === 'all' ? setSearchParams({}) : setSearchParams({ filter: newFilter });
  };

  const handleJoin = async (groupId) => {
    try {
      await api.joinGroup(groupId);
      setGroups(groups.map(g => g.id === groupId ? { ...g, is_member: !g.is_member, member_count: g.is_member ? (g.member_count - 1) : (g.member_count + 1) } : g));
      toast.success('Group membership updated!');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to join group'); }
  };

  const handleCreateGroup = async (groupData) => {
    const newGroup = await api.createGroup(groupData);
    setGroups([newGroup, ...groups]);
  };

  const handleViewDetail = (group) => {
    const isMember = group.is_member || group.creator_id === user?.id;
    if (isMember) {
      navigate(`/groups/${group.id}`);
    } else {
      setDetailGroup(group);
    }
  };

  // Filter by search
  const filtered = groups.filter(g => !search || g.name?.toLowerCase().includes(search.toLowerCase()));

  // Split into sections by type
  const myGroups = filtered.filter(g => g.is_member || g.creator_id === user?.id);
  const recommendedGroups = filtered.filter(g =>
    g.location?.toLowerCase().includes((user?.city || '').toLowerCase()) ||
    g.group_type === 'industry'
  );
  const professionGroups = filtered.filter(g => g.group_type === 'industry');
  const locationGroups = filtered.filter(g => g.group_type === 'location');
  const genderGroups = filtered.filter(g => g.group_type === 'gender');
  const interestGroups = filtered.filter(g => g.group_type === 'interest');

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-muted rounded-lg w-1/3" />
          {[1, 2].map(i => (
            <div key={i} className="space-y-3">
              <div className="h-6 bg-muted rounded w-48" />
              <div className="flex gap-5">{[1,2,3,4].map(j => <div key={j} className="flex-shrink-0 w-[340px] h-72 bg-muted rounded-xl" />)}</div>
            </div>
          ))}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="groups-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Outfit']">Network Groups</h1>
            <p className="text-muted-foreground text-sm">Join communities of professionals who share your interests</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search groups..." className="pl-10 w-48" data-testid="group-search" />
            </div>
            <Link to="/learn">
              <Button variant="outline" size="sm" data-testid="learning-btn"><BookOpen className="w-4 h-4 mr-1.5" />Learning</Button>
            </Link>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-group-btn"><Plus className="w-4 h-4 mr-1.5" />Create Group</Button>
          </div>
        </div>

        {/* ——— My Groups ——— */}
        {myGroups.length > 0 && (
          <GroupSectionRow
            title="My Groups"
            subtitle="Groups you've joined or created"
            icon={Users}
            iconColor="bg-primary/10 text-primary"
            groups={myGroups}
            currentUserId={user?.id}
            onJoin={handleJoin}
            onManage={g => setManageGroup(g)}
            onViewDetail={handleViewDetail}
            emptyMsg=""
          />
        )}

        {/* ——— Recommended ——— */}
        <GroupSectionRow
          title="Recommended for You"
          subtitle="Based on your location and industry"
          icon={Sparkles}
          iconColor="bg-amber-500/10 text-amber-500"
          groups={recommendedGroups}
          currentUserId={user?.id}
          onJoin={handleJoin}
          onManage={g => setManageGroup(g)}
          onViewDetail={g => setDetailGroup(g)}
          emptyMsg="No recommended groups yet. Create one to get started!"
        />

        {/* ——— By Profession ——— */}
        <GroupSectionRow
          title="Groups by Profession"
          subtitle="Connect with peers in your field"
          icon={Briefcase}
          iconColor="bg-blue-500/10 text-blue-500"
          groups={professionGroups}
          currentUserId={user?.id}
          onJoin={handleJoin}
          onManage={g => setManageGroup(g)}
          onViewDetail={g => setDetailGroup(g)}
          emptyMsg="No profession groups yet"
        />

        {/* ——— By Location ——— */}
        <GroupSectionRow
          title="Groups by Location"
          subtitle="Local professional communities"
          icon={MapPin}
          iconColor="bg-green-500/10 text-green-500"
          groups={locationGroups}
          currentUserId={user?.id}
          onJoin={handleJoin}
          onManage={g => setManageGroup(g)}
          onViewDetail={g => setDetailGroup(g)}
          emptyMsg="No location groups yet"
        />

        {/* ——— By Gender ——— */}
        <GroupSectionRow
          title="Groups by Gender"
          subtitle="Communities for specific audiences"
          icon={Users}
          iconColor="bg-pink-500/10 text-pink-500"
          groups={genderGroups}
          currentUserId={user?.id}
          onJoin={handleJoin}
          onManage={g => setManageGroup(g)}
          onViewDetail={g => setDetailGroup(g)}
          emptyMsg="No gender-specific groups yet"
        />

        {/* ——— By Interest ——— */}
        {interestGroups.length > 0 && (
          <GroupSectionRow
            title="Groups by Interest"
            subtitle="Communities around shared interests"
            icon={Star}
            iconColor="bg-violet-500/10 text-violet-500"
            groups={interestGroups}
            currentUserId={user?.id}
            onJoin={handleJoin}
            onManage={g => setManageGroup(g)}
            onViewDetail={handleViewDetail}
            emptyMsg=""
          />
        )}

        {/* Empty State */}
        {filtered.length === 0 && (
          <Card className="py-12">
            <CardContent className="text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No groups found</h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">Start a community for professionals in your area or industry.</p>
              <Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4 mr-2" />Create a Group</Button>
            </CardContent>
          </Card>
        )}

        {showCreate && <CreateGroupModal onClose={() => setShowCreate(false)} onCreate={handleCreateGroup} />}
        {manageGroup && <ManageGroupModal group={manageGroup} onClose={() => setManageGroup(null)} onUpdate={() => fetchGroups(filter)} />}
        {detailGroup && <GroupDetailModal group={detailGroup} currentUserId={user?.id} onClose={() => setDetailGroup(null)} onJoin={handleJoin} onManage={g => setManageGroup(g)} onNavigate={(id) => navigate(`/groups/${id}`)} />}
      </div>
    </Layout>
  );
};

export default Groups;
